import api from './client';
import type { Vertical } from '@/lib/vertical';
import type { AsomPlan } from './asoMarket';
import { useAuthStore } from '../store/auth';
import { API_FETCH_URL } from './base';

// A hand-maintained AdMob key set (migration 126, admob_manual_apps). Replaces
// the old OAuth sync: developers keep this pool by hand and connect entries to
// apps on the Connect tab.
export interface ManualAdmobApp {
  id: number;
  play_console: string | null;
  admob_app_id: string;
  display_name: string | null;
  platform: 'android' | 'ios';
  bundle_id: string | null;
  banner_unit_id: string | null;
  interstitial_unit_id: string | null;
  rewarded_unit_id: string | null;
  connected: boolean;
  connected_app_name: string | null;
  created_at: number;
  updated_at: number;
}

export interface ManualAdmobInput {
  play_console?: string;
  admob_app_id: string;
  display_name?: string;
  platform: 'android' | 'ios';
  bundle_id?: string;
  banner_unit_id?: string;
  interstitial_unit_id?: string;
  rewarded_unit_id?: string;
}

export interface AdmobPickerApp {
  name: string;
  appId: string;
  platform: 'IOS' | 'ANDROID';
  displayName: string | null;
  packageName: string | null;
  approvalState: string | null;
  adUnits: { banner: string | null; interstitial: string | null; rewarded: string | null };
  adUnitCount: number;
}

export interface ImportResult {
  app_id: string;
  api_key: string;
  admob_app_id: string;
  name: string;
  platform: string;
  bundle_id: string;
  banner_unit_id: string | null;
  interstitial_unit_id: string | null;
  rewarded_unit_id: string | null;
  firebase_project_id?: string | null;
  firebase_error?: string | null;
  // Set when a new Firebase project is being created for the import; the
  // history card follows the run and shows the project once it lands.
  firebase_provision_id?: number | null;
  privacy_policy_url?: string | null;
  privacy_policy_error?: string | null;
}

export interface ImportRow extends ImportResult {
  id: number;
  admob_app_display: string | null;
  firebase_project_id: string | null;
  firebase_android_app_id: string | null;
  firebase_registered_at: number | null;
  // Latest "new Firebase project" run for this import, if any.
  firebase_provision_id: number | null;
  firebase_provision_state: FirebaseProvisionState | null;
  firebase_provision_error: string | null;
  firebase_provision_project_id: string | null;
  app_name: string | null;
  contact_email: string | null;
  privacy_policy_id: string | null;
  privacy_policy_url: string | null;
  privacy_policy_generated_at: number | null;
  imported_by: number | null;
  created_at: number;
  // Marketing assets from the linked app (created on the Create tab).
  icon_url: string | null;
  short_description: string | null;
  full_description: string | null;
}

export interface ImportInput {
  admob_app_id: string;
  app_id?: string;
  name?: string;
  bundle_id?: string;
  link_existing?: boolean;
  firebase_account_id?: number;
  // Create a fresh Firebase project in this organisation instead.
  firebase_org_id?: number;
  firebase_browser_profile_id?: number;
  email?: string;
  owner_admin_id?: string;
}

export function listManualAdmob(): Promise<ManualAdmobApp[]> {
  return api.get<ManualAdmobApp[]>('/admin/apps-setup/admob/manual');
}
export function createManualAdmob(input: ManualAdmobInput): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/apps-setup/admob/manual', input);
}
export function updateManualAdmob(id: number, input: ManualAdmobInput): Promise<{ message: string }> {
  return api.patch<{ message: string }>(`/admin/apps-setup/admob/manual/${id}`, input);
}
export function deleteManualAdmob(id: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/apps-setup/admob/manual/${id}`);
}
export function getAdmobApps(): Promise<AdmobPickerApp[]> {
  return api.get<AdmobPickerApp[]>('/admin/apps-setup/admob/apps');
}
export function importAdmobApp(input: ImportInput): Promise<ImportResult> {
  return api.post<ImportResult>('/admin/apps-setup/import', input);
}
export function getImports(): Promise<ImportRow[]> {
  return api.get<ImportRow[]>('/admin/apps-setup/imports');
}

export interface FirebaseAccount { id: number; name: string; }
// projects = runs that hold a slot (see ORG_PROJECTS_COUNT_SQL in the API);
// the picker shows "used/max_projects" and hides organisations that are full.
// Where a new project can be made: an organisation (API route, `org`) or a
// browser profile with the firebase scheme (the runner, `bp`).
export interface FirebaseOrg { id: number; name: string; projects: number; max_projects: number; kind?: 'org' | 'bp'; }
export type FirebaseProvisionState = 'new' | 'project' | 'key' | 'firebase' | 'analytics' | 'services' | 'sa' | 'ready';

// The Firebase picker offers existing projects and "new project in <org or
// browser profile>"; one string keeps them all in a single <select> value.
export type FirebaseChoice = `acc:${number}` | `org:${number}` | `bp:${number}`;
export function firebaseChoiceToInput(choice: FirebaseChoice | '' | undefined): Pick<ImportInput, 'firebase_account_id' | 'firebase_org_id' | 'firebase_browser_profile_id'> {
  if (!choice) return {};
  const [kind, id] = choice.split(':');
  if (kind === 'org') return { firebase_org_id: Number(id) };
  if (kind === 'bp') return { firebase_browser_profile_id: Number(id) };
  return { firebase_account_id: Number(id) };
}

export function getFirebaseAccounts(): Promise<FirebaseAccount[]> {
  return api.get<FirebaseAccount[]>('/admin/apps-setup/firebase/accounts');
}
// Organisations and Firebase browser profiles in one list: the picker's "new
// project" side does not care which route a place takes.
export async function getFirebaseOrgs(): Promise<FirebaseOrg[]> {
  const [orgs, profiles] = await Promise.all([
    api.get<FirebaseOrg[]>('/admin/apps-setup/firebase/orgs'),
    api.get<Array<{ id: number; name: string; google_email: string | null; projects: number; max_projects: number; console_name: string | null }>>(
      '/admin/apps-setup/firebase/profiles',
    ).catch(() => []),
  ]);
  return [
    ...orgs.map((o) => ({ ...o, kind: 'org' as const })),
    ...profiles.map((p) => ({
      // A shared console's profile: its Firebase is for that console's apps.
      id: p.id, name: p.console_name ? `${p.name} · browser · console ${p.console_name} only` : `${p.name} · browser`,
      projects: p.projects, max_projects: p.max_projects, kind: 'bp' as const,
    })),
  ];
}
export function registerImportFirebase(
  id: number, choice: FirebaseChoice,
): Promise<{ firebase_project_id?: string; firebase_android_app_id?: string; firebase_provision_id?: number }> {
  return api.post(`/admin/apps-setup/import/${id}/firebase`, firebaseChoiceToInput(choice));
}
export function generateImportPrivacyPolicy(id: number, email: string): Promise<{ id: string; url: string }> {
  return api.post(`/admin/apps-setup/import/${id}/privacy-policy`, { email });
}
export async function downloadGoogleServices(id: number): Promise<void> {
  const base = API_FETCH_URL;
  const token = useAuthStore.getState().token;
  const res = await fetch(`${base}/admin/apps-setup/import/${id}/google-services.json`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'google-services.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface BundleSuggestInput {
  name: string;
  short_description?: string;
  full_description?: string;
  /** The app's brand, so the id reflects the theme and never the brand. */
  brand?: string;
}

export interface CreateSkeletonInput {
  name: string;
  short_description?: string;
  full_description?: string;
  bundle_id: string;
  icon_url?: string;
  platform: 'android' | 'ios';
  owner_admin_id?: string;
  /** MEDIA keys of the frames drawn on the Create tab; saved as the app's store screenshots. */
  screenshot_keys?: string[];
  /** MEDIA key of the feature graphic (uploaded or rendered); saved with the app. */
  feature_graphic_key?: string;
  /** The base build stub picked on the Create tab; seeds the app's build config. */
  base_repo_id?: number;
  /** AI App Wizard job the draft came from; its localizations are saved with the app as a listing package. */
  wizard_job_id?: string;
  /** ASO.Market orders on release: priority countries and keywords. */
  asom_plan?: AsomPlan;
}

export interface TemplateDeployResult {
  tracker_campaign_ids: string[];
  ad_campaign_ids: string[];
  ads_count: number;
  force_offer_url: string | null;
  warning?: string;
}

export interface CreateSkeletonResult {
  app_id: string;
  api_key: string;
  name: string;
  bundle_id: string;
  icon_url: string | null;
  platform: string;
  status: string;
  screenshots_attached: number;
  feature_graphic_attached: boolean;
  template?: TemplateDeployResult;
  /** Android build prep done at creation time; null/false when a step failed or the app is iOS. */
  build?: { base_repo_id: number | null; keystore_generated: boolean };
  /** Present when a wizard_job_id was sent and its localizations were stored as a listing package. */
  listing_package?: { locales: number; screenshots: number };
}

export interface Skeleton {
  app_id: string;
  owner_admin_id: string | null;
  name: string;
  bundle_id: string;
  icon_url: string | null;
  created_at: string;
}

export function suggestBundle(input: BundleSuggestInput): Promise<{ bundle_id: string }> {
  return api.post<{ bundle_id: string }>('/admin/apps-setup/create/suggest-bundle', input);
}
/** One Google NL target category and how the full description scored on it. */
export interface GnlScoreDetail { id: string; label: string; name: string | null; confidence: number; ok: boolean; /** The bar this category had to clear (Gambling/Other 0.8, the rest 0.5). */ min?: number }
/** The Google NL gate result; `score` is null when no key is configured. */
export interface GnlGate { attempts: number; score: { ok: boolean; details: GnlScoreDetail[]; summary: string } | null }

/** The brand pack the copy prompts take; the name doubles as the brand when omitted. */
export interface CopyBrandInput { brand?: string; vertical?: Vertical | null; keywords?: string; meta_tone?: MetaTone }

export function generateDescriptions(
  input: { name: string; notes?: string; protected_terms?: string } & CopyBrandInput,
): Promise<{ short_description: string; full_description: string; gnl: GnlGate }> {
  return api.post<{ short_description: string; full_description: string; gnl: GnlGate }>('/admin/apps-setup/create/descriptions', input);
}

/** What the icon concept step settled on before drawing. */
export interface IconConcept { concept: string; solid_background: string; motif: string }
/** The brand pack an artwork prompt takes: solid background + visual identity. */
export interface ArtBrandInput { brand?: string; brand_bg?: string; brand_style?: string }

export function generateIcon(
  input: {
    prompt: string; stub_id?: number; logo_key?: string;
    /** What to change after seeing candidates; with base_icon_url that picked icon is edited instead of redrawn. */
    correction?: string; base_icon_url?: string;
  } & ArtBrandInput,
): Promise<{ candidates: string[]; concept: IconConcept }> {
  return api.post<{ candidates: string[]; concept: IconConcept }>('/admin/apps-setup/create/icon', input);
}
export function createSkeleton(input: CreateSkeletonInput): Promise<CreateSkeletonResult> {
  return api.post<CreateSkeletonResult>('/admin/apps-setup/create', input);
}

/** The rest of the package for an app the wizard created early (name, bundle, icon only). */
export interface CompleteSkeletonInput {
  name?: string;
  short_description?: string;
  full_description?: string;
  /** A rerolled icon; the next build picks it up. */
  icon_url?: string;
  screenshot_keys?: string[];
  feature_graphic_key?: string;
  wizard_job_id?: string;
}
export type CompleteSkeletonResult = Pick<
  CreateSkeletonResult,
  'app_id' | 'name' | 'bundle_id' | 'icon_url' | 'screenshots_attached' | 'feature_graphic_attached' | 'listing_package'
>;
export function completeSkeleton(appId: string, input: CompleteSkeletonInput): Promise<CompleteSkeletonResult> {
  return api.post<CompleteSkeletonResult>(`/admin/apps-setup/create/${encodeURIComponent(appId)}/complete`, input);
}
// ---- Creation-time screenshots (gpt-image-2) ----
// A brand-new app has no Play listing to plan from, so the scenes are anchored
// on the base build stub the app will ship. Two stages, like the Play panel:
// plan once, then one image per call so a five-frame set stays responsive.

export interface CreateStub {
  id: number; name: string; notes: string | null;
  /** The repo type and its domain rule (Repos Setup tab). */
  type_name: string | null; domain: DomainRule | null;
}
export type DomainRule = 'req' | 'opt' | 'off';
export interface CreateScreenshotScene { caption: string; scene: string }
/**
 * `subject` is what the app is, in one line — send it back with every frame,
 * together with the resolved brand pack (brand / brandBg / brandStyle /
 * phoneMockups) so each frame is drawn in the same identity as the plan.
 */
export interface CreateScreenshotPlan {
  style: string; subject: string; scenes: CreateScreenshotScene[];
  brand: string; brandBg: string; brandStyle: string; phoneMockups: boolean;
  shotOrient: ShotOrient;
  /** Real frames the set is drawn around (scene i ↔ frame i); 0 when none. */
  uiRefCount: number;
}
/** Brand pack fields a screenshot request carries (snake_case on the wire). */
export interface ShotBrandInput extends ArtBrandInput { phone_mockups?: boolean; shot_orient?: ShotOrient }
/** The brand pack of a plan, ready to spread into a per-frame request. */
export function shotBrandOfPlan(plan: Pick<CreateScreenshotPlan, 'brand' | 'brandBg' | 'brandStyle' | 'phoneMockups' | 'shotOrient'> | null | undefined): ShotBrandInput {
  if (!plan) return {};
  return { brand: plan.brand, brand_bg: plan.brandBg, brand_style: plan.brandStyle, phone_mockups: plan.phoneMockups, shot_orient: plan.shotOrient };
}
/** The same from a project / job brand pack; empty when the pack has no brand. */
export function shotBrandOf(pack: Pick<BrandPack, 'brand' | 'brand_bg' | 'brand_style' | 'phone_mockups' | 'shot_orient'> | null | undefined): ShotBrandInput {
  if (!pack?.brand) return {};
  return { brand: pack.brand, brand_bg: pack.brand_bg, brand_style: pack.brand_style, phone_mockups: pack.phone_mockups, shot_orient: pack.shot_orient };
}

export function listCreateStubs(): Promise<CreateStub[]> {
  return api.get<CreateStub[]>('/admin/apps-setup/create/stubs');
}
export function planCreateScreenshots(input: {
  name?: string; short_description?: string; full_description?: string;
  brief?: string; stub_id?: number; count: number; keywords?: string;
  /** Draw AI phone mockups around the stub's real frames (needs stub_id with a library). */
  ui_refs?: boolean;
} & ShotBrandInput): Promise<CreateScreenshotPlan> {
  return api.post<CreateScreenshotPlan>('/admin/apps-setup/create/screenshot-plan', input);
}
export function generateCreateScreenshot(input: {
  style: string; scene: string; caption: string; index: number; total: number; subject: string;
  /** Stub library frame to restage on the phone (mockup mode with refs). */
  ref_key?: string;
  /** How many frames the whole set has, so a scene past them keeps the mockup with invented UI. */
  ui_ref_count?: number;
} & ShotBrandInput): Promise<{ key: string; url: string; contentType: string }> {
  return api.post<{ key: string; url: string; contentType: string }>('/admin/apps-setup/create/screenshot', input);
}
/**
 * The feature graphic before the app exists: drawn in the run's style (when
 * given), stored under the feature prefix like an uploaded banner. No scene =
 * the AI plans tagline + composition; a tagline given (even '') is kept.
 */
export function generateCreateFeatureGraphic(input: {
  name: string; short_description?: string; full_description?: string;
  brief?: string; stub_id?: number; keywords?: string;
  style?: string; subject?: string; tagline?: string; scene?: string;
} & ShotBrandInput): Promise<WizardBannerResult> {
  return api.post<WizardBannerResult>('/admin/apps-setup/create/feature-graphic', input);
}

export function listSkeletons(): Promise<Skeleton[]> {
  return api.get<Skeleton[]>('/admin/apps-setup/skeletons');
}

// ---- AI App Wizard ----
// One background job generates the whole app draft (copy + icon + screenshots)
// as independent tasks; the modal polls the job and fills cards as tasks land.

/**
 * The brand pack of a project: what the copy must carry (brand, vertical →
 * listing angle, keywords) and what the artwork must look like (solid
 * background, visual identity, phone mockups). Empty strings mean "not set".
 */
export interface BrandPack {
  brand: string;
  vertical: Vertical | null;
  keywords: string;
  /** Default operator brief for artwork, used when a run's brief is empty. */
  brief: string;
  brand_bg: string;
  brand_style: string;
  phone_mockups: boolean;
  /** Landscape 16:9 store screenshots (Roulette) or portrait 9:16. */
  shot_orient: ShotOrient;
  /** R2 key of the official logo; the icon is generated as an edit of it when set. */
  logo_key: string;
  /** Listing vocabulary: the usual social-casino words, or safe (never casino / money outside the brand name). */
  meta_tone: MetaTone;
}

export type ShotOrient = 'portrait' | 'landscape';
export type MetaTone = 'aggressive' | 'safe';

export interface AppProject extends BrandPack {
  id: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
  logo_url: string | null;
}

/** A brand preset from the prompt lab — fills a new project's fields. */
export interface BrandPreset extends BrandPack {
  name: string;
  prompt: string;
  title: string;
  short_description: string;
  logo_url: string | null;
}

export function listAppProjects(): Promise<AppProject[]> {
  return api.get<AppProject[]>('/admin/apps-setup/projects');
}
export function listBrandPresets(): Promise<BrandPreset[]> {
  return api.get<BrandPreset[]>('/admin/apps-setup/projects/presets');
}
export function createAppProject(input: { name: string; prompt: string } & Partial<BrandPack>): Promise<AppProject> {
  return api.post<AppProject>('/admin/apps-setup/projects', input);
}
export function updateAppProject(id: string, input: { name?: string; prompt?: string } & Partial<BrandPack>): Promise<AppProject> {
  return api.patch<AppProject>(`/admin/apps-setup/projects/${id}`, input);
}
export function deleteAppProject(id: string): Promise<null> {
  return api.delete<null>(`/admin/apps-setup/projects/${id}`);
}

/** A real frame of the game shipped by a build stub — the repo screenshot library. */
export interface StubScreenshot {
  id: number;
  /** R2 key — what an AI-mockup run passes back as the frame's UI reference. */
  key: string;
  url: string;
  position: number;
  notes: string | null;
}

export function listStubScreenshots(stubId: number): Promise<StubScreenshot[]> {
  return api.get<StubScreenshot[]>(`/admin/apps-setup/create/stubs/${stubId}/screenshots`);
}

/** What a creation-time image is uploaded as — it decides the MEDIA prefix. */
export type CreateAssetKind = 'screenshot' | 'feature' | 'icon' | 'logo';

/**
 * Raw image upload (same shape as the repo screenshot upload in app-builds).
 * Used both for frames the browser composes and for files the operator picks
 * by hand in the manual wizard.
 */
export async function uploadCreateAsset(
  blob: Blob,
  kind: CreateAssetKind = 'screenshot',
): Promise<{ key: string; url: string }> {
  const base = API_FETCH_URL;
  const token = useAuthStore.getState().token;
  const res = await fetch(`${base}/admin/apps-setup/create/screenshot-upload?kind=${kind}`, {
    method: 'POST',
    body: blob,
    headers: {
      // The server sniffs the bytes anyway; a picked file keeps its own type.
      'Content-Type': blob.type || 'image/png',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const json = await res.json();
  if (!res.ok || !json?.success) throw new Error(json?.error || `Upload failed ${res.status}`);
  return json.data as { key: string; url: string };
}

export function uploadCreateScreenshot(blob: Blob): Promise<{ key: string; url: string }> {
  return uploadCreateAsset(blob, 'screenshot');
}

export type WizardShotSource = 'ai' | 'repo';
export type WizardJobStatus = 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
export type WizardTaskStatus = 'pending' | 'running' | 'done' | 'failed';

export interface WizardCopyResult {
  name: string;
  short_description: string;
  full_description: string;
  bundle_id: string;
  /** Present when the Google NL gate ran on the full description. */
  gnl?: GnlGate;
}
export interface WizardIconResult { candidates: string[]; concept?: IconConcept }
export interface WizardScreenshotResult { key: string; url: string; scene: string; caption: string; ref_key?: string }
/** The AI feature graphic of a run (1024×500, under the feature prefix) and what it was drawn from. */
export interface WizardBannerResult { key: string; url: string; tagline: string; scene: string; style: string }
/** One localized listing text; `fits: false` — the text overflows Play limits and needs a trim later. */
export interface WizardLocTextResult {
  lang: string;
  title: string;
  short_description: string;
  full_description: string;
  fits: boolean;
  /** Protected names the translation still lost after the restore pass. */
  missing_terms?: string[];
}
/** One localized screenshot render. */
export interface WizardLocShotResult { lang: string; key: string; url: string; caption: string; position: number }

export interface WizardTask {
  id: string;
  kind: 'copy' | 'icon' | 'screenshot' | 'banner' | 'loc_text' | 'loc_shot';
  position: number;
  status: WizardTaskStatus;
  error: string | null;
  result: WizardCopyResult | WizardIconResult | WizardScreenshotResult | WizardBannerResult | WizardLocTextResult | WizardLocShotResult | null;
  /** loc_* tasks only: the target locale, present even while the task is pending. */
  lang?: string;
}

export interface WizardJob {
  id: string;
  status: WizardJobStatus;
  shot_source: WizardShotSource;
  shot_count: number;
  base_repo_id: number | null;
  style: string | null;
  subject: string | null;
  created_at: string;
  finished_at: string | null;
  /** The brand pack frozen from the project when the job was created. */
  brand: string;
  vertical: Vertical | null;
  keywords: string;
  brand_bg: string;
  brand_style: string;
  phone_mockups: boolean;
  shot_orient: ShotOrient;
  logo_key: string;
  meta_tone: MetaTone;
  /** Real frames the run's screenshots were drawn around; 0 for plain AI frames. */
  ui_ref_count: number;
  /** The app already created from this job (early, from the wizard screen), if any. */
  app_id: string | null;
  /** Step 5 brief and step 6 text instructions / untranslatable names of this run. */
  brief?: string;
  meta_notes?: string;
  protected_terms?: string;
  /** The icon-only prompt typed on the setup screen; empty when the icon follows the brief. */
  icon_prompt?: string;
}

/** The app a job already created, as it is now — the operator's name, bundle, texts and icon. */
export interface WizardJobApp { name: string; bundle_id: string; short_description: string; full_description: string; icon_url: string | null }
export interface WizardJobDetail { job: WizardJob; tasks: WizardTask[]; app?: WizardJobApp | null }

export type WizardPlan = CreateScreenshotPlan;

export function createWizardJob(input: {
  project_id: string;
  base_repo_id?: number;
  shot_source: WizardShotSource;
  shot_count?: number;
  brief?: string;
  ui_refs?: boolean;
  /** Step 6: instructions for the texts, keywords for this run (replace the project's), names never to translate. */
  meta_notes?: string;
  keywords?: string;
  protected_terms?: string;
  /** Replaces the brief and the stub's game for the icon only. */
  icon_prompt?: string;
  /** ASO.Market orders on release, copied to the app the job creates. */
  asom_plan?: AsomPlan;
}): Promise<{ job_id: string; plan: WizardPlan }> {
  return api.post<{ job_id: string; plan: WizardPlan }>('/admin/apps-setup/wizard/jobs', input);
}
export function getWizardJob(id: string): Promise<WizardJobDetail> {
  return api.get<WizardJobDetail>(`/admin/apps-setup/wizard/jobs/${id}`);
}
export function getActiveWizardJob(): Promise<{ job_id: string | null }> {
  return api.get<{ job_id: string | null }>('/admin/apps-setup/wizard/jobs?active=1');
}
/** The admin's most recent job, running or not. */
export function getLastWizardJob(): Promise<{ job_id: string | null }> {
  return api.get<{ job_id: string | null }>('/admin/apps-setup/wizard/jobs');
}
export function cancelWizardJob(id: string): Promise<null> {
  return api.post<null>(`/admin/apps-setup/wizard/jobs/${id}/cancel`);
}

/** Caption of one current screenshot, sent as the source of every localized render. */
export interface WizardLocalizeCaption { key?: string; url: string; caption: string; position: number }

export interface WizardLocalizeInput {
  langs: string[];
  title: string;
  short_description: string;
  full_description: string;
  captions: WizardLocalizeCaption[];
}

/** 409 — the job is not done yet or a localization run is already in progress. */
export function localizeWizardJob(jobId: string, input: WizardLocalizeInput): Promise<{ task_count: number }> {
  return api.post<{ task_count: number }>(`/admin/apps-setup/wizard/jobs/${jobId}/localize`, input);
}
